import { Trans } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { postMessageToMobileApp } from 'components/app/helpers';
import { redirectToEmailVerification } from 'components/Auth/Common/helpers';
import InfoBox from 'components/Auth/Common/InfoBox';
import { NewRedirectIcon } from 'components/customicons/holdings';
import Render from 'components/render';
import { RenderForIndianExchange } from 'components/render/RenderByExchangeType';
import Seo from 'components/seo';
import {
  IS_INDIAN_EXCHANGE,
  isRNWebview,
  IS_RN_WEBVIEW_IOS,
  SITE_ROOT,
  TESTNET_URL,
  IND_DOMAIN_SWITCH,
} from 'constants/constants';
import { POST_MESSAGE_TO_MOBILE_EVENTS } from 'constants/enums';
import { SIGNUP_CARDS_FLAG } from 'constants/featureFlag';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import routes from 'constants/routes';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { getMobileOperatingSystem } from 'helpers/utils';
import i18n from 'i18n/config';
import { Info, ValidationCorrect } from 'styles/assets/icons';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';

import { SignUpInfoList } from './Messages.constants';
import styles from './styles.module.scss';

const AlreadyRegisteredMessage = () => {
  const onClickAlreadyRegisteredUserResetPassword = () => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SET_OR_CHANGE_PASSWORD, {
      Action: 'set',
      page_name: 'Web App',
      Operating_system: getMobileOperatingSystem(),
    });
  };
  return (
    <div className={`${styles.errorMessage} ${styles.alreadyRegistered}`}>
      <span>{i18n.t('auth:accountExistsError')}</span>
      <span className={styles.loginOrReset}>
        <Link
          className={styles.link}
          to={routes.login}
          data-testid="alreadyRegistered-loginPageLink"
        >
          {i18n.t('auth:logIn') as string}
        </Link>
        <span className={styles.or}> {` ${i18n.t('auth:or')} `}</span>
        <Link
          className={styles.link}
          to={routes.forgotPassword}
          onClick={onClickAlreadyRegisteredUserResetPassword}
          data-testid="alreadyRegistered-forgotPasswordLink"
        >
          {i18n.t('auth:resetPassword') as string}
        </Link>
      </span>
    </div>
  );
};

const AlreadyRegisteredUnverifiedMessage = ({ email }: { email: string }) => {
  const history = useHistory();

  const onClickVerifyNow = () => {
    redirectToEmailVerification(email, history);
  };

  return (
    <div className={styles.errorMessage}>
      {i18n.t('errors:user.already_registered')}.{' '}
      <Box
        as="span"
        className={`${styles.link} ${styles.verifyNow}`}
        onClick={onClickVerifyNow}
        role="button"
        data-testid="signup-verifyLink"
      >
        {i18n.t('auth:verifyNow')}
      </Box>
    </div>
  );
};

const goToTestnet = () => {
  if (IS_RN_WEBVIEW_IOS) {
    postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.OPEN_LINK_IN_EXTERNAL_BROWSER, {
      url: TESTNET_URL,
    });
    return;
  }
  window.open(TESTNET_URL, '_blank');
};

const CountryNotServedMessage = () => {
  return (
    <div className={styles.countryMessage}>
      <div className={styles.important}>
        <Info />
        <span className={styles.countryInfoImportant}>{i18n.t('auth:important')}</span>
      </div>

      <div className={styles.countryInfoTextBox}>
        <p>
          {i18n.t('auth:countryNotServed')}
          <DeltaButton
            className={styles.link}
            wrapperClassName={styles.testnet}
            onClick={goToTestnet}
            data-testid="testNetLink"
            useBasicButton
          >
            {i18n.t('trading:demoTrading')} <NewRedirectIcon />
          </DeltaButton>
          .
        </p>
      </div>
    </div>
  );
};

const IndividualInvestorsCountryMessage = () => {
  return (
    <div className={styles.countryMessage}>
      <div className={styles.important}>
        <Info />
        <span className={styles.countryInfoImportant}>{i18n.t('auth:important')}</span>
      </div>

      <div className={styles.countryInfoTextBox}>
        <p>{i18n.t('auth:individualInvestorsCountryMessage')}</p>
      </div>
    </div>
  );
};

const DiscountAppliedMessage = (props: {
  referralCode?: string;
  discountPercentage: number;
}) => {
  const { referralCode, discountPercentage } = props;

  return (
    <div className={`${styles.discountText} ${styles.successMessage}`}>
      <ValidationCorrect />
      <Render
        when={isRNWebview()}
        else={
          discountPercentage > 0
            ? i18n.t('auth:signUpDiscount', {
                referralCode,
                discountPercentage,
              })
            : i18n.t('auth:referralCodeApplied', {
                referralCode,
              })
        }
      >
        {discountPercentage > 0
          ? i18n.t('auth:discountApplied', {
              discountPercentage,
            })
          : i18n.t('auth:referralCodeApplied', {
              referralCode,
            })}
      </Render>
    </div>
  );
};

const InvalidReferralCodeMessage = () => {
  return (
    <div className={`${styles.discountText} ${styles.invalidMessage}`}>
      {i18n.t('auth:invalidReferralCode')}
    </div>
  );
};

interface EmailAutoCorrectProps {
  email: string;
  updateEmailAndRemoveSuggestion: () => void;
}

const EmailAutoCorrect = ({
  email,
  updateEmailAndRemoveSuggestion,
}: EmailAutoCorrectProps) => {
  return (
    <div className={styles.emailAutoCorrect}>
      <span className={styles.phrase}>{i18n.t('auth:didYouMean')}</span>
      <span
        data-testid="autocorrect-email"
        className={styles.email}
        onClick={updateEmailAndRemoveSuggestion}
        aria-hidden="true"
      >
        {email}?
      </span>
    </div>
  );
};

const ExistingUserLoginMessage = () => {
  const { state } = useLocation();
  const { showScalpingPopup, from } = state || {};
  const { pathname } = from || {};

  let linkState = { pathname: routes.login };

  if (showScalpingPopup) {
    linkState = {
      ...linkState,
      state: {
        showScalpingPopup,
        from: {
          pathname,
        },
      },
    };
  }

  return (
    <div className={styles.existingUser}>
      <Trans
        i18nKey="auth:existingUser"
        components={{
          login: (
            <Link className={styles.link} to={linkState} data-testid="loginPageLink" />
          ),
        }}
      />
    </div>
  );
};

const InfoBoxes = () => {
  const { INFO_LIST } = SignUpInfoList();
  return INFO_LIST[SIGNUP_CARDS_FLAG]?.map(
    ({ description, header, id, imageSrc, imageClassName, alt }) => {
      return (
        <InfoBox
          key={id}
          testId={`signupInfoBox-${id}`}
          header={header}
          description={description}
          image={<img src={imageSrc} className={imageClassName} alt={alt} />}
        />
      );
    }
  );
};

const TermsAndConditionsMessage = () => {
  const links = {
    termsOfUse: `${SITE_ROOT}/terms-of-use`,
    privacyPolicy: `${SITE_ROOT}/privacy-policy`,
  };

  return (
    <div className={styles.termCondition} data-testid="signupTermsAndConditions">
      {i18n.t('auth:agreeToDelta')}{' '}
      <a href={links.termsOfUse} target="__blank" data-testid="termsOfUseLink">
        {i18n.t('auth:termsOfUse')}{' '}
      </a>
      {i18n.t('common:and')}{' '}
      <a href={links.privacyPolicy} target="__blank" data-testid="privacyPolicyLink">
        {i18n.t('auth:privacyPolicy')}
      </a>
      .
    </div>
  );
};

const SignupSEO = () => {
  let title = `${i18n.t('auth:signUp')} | ${i18n.t('auth:register')} - ${
    IS_INDIAN_EXCHANGE ? i18n.t('common:titleIndian') : i18n.t('common:title')
  }`;

  let description = IS_INDIAN_EXCHANGE
    ? i18n.t('auth:seoRegisterIndian')
    : i18n.t('auth:seoRegister');

  if (IND_DOMAIN_SWITCH) {
    title = `${
      IS_INDIAN_EXCHANGE ? i18n.t('auth:signUp') : i18n.t('auth:signUpRegister')
    } | ${
      IS_INDIAN_EXCHANGE ? i18n.t('common:newTitleIndian') : i18n.t('common:newTitle')
    }`;
    description = IS_INDIAN_EXCHANGE
      ? i18n.t('auth:newSeoRegisterIndian')
      : i18n.t('auth:newSeoRegister');
  }

  return <Seo title={title} description={description} />;
};

const AvoidRedoKYC = () => {
  return (
    <RenderForIndianExchange>
      <Box className={styles.avoidRedoKYCMessage}>
        <Trans i18nKey="auth:avoidRedoKYC">
          Already a Delta Exchange user?
          <br />
          Sign up with the same email to avoid re-doing KYC.
        </Trans>
      </Box>
    </RenderForIndianExchange>
  );
};

export {
  AlreadyRegisteredMessage,
  AlreadyRegisteredUnverifiedMessage,
  CountryNotServedMessage,
  DiscountAppliedMessage,
  InvalidReferralCodeMessage,
  EmailAutoCorrect,
  ExistingUserLoginMessage,
  InfoBoxes,
  TermsAndConditionsMessage,
  SignupSEO,
  IndividualInvestorsCountryMessage,
  AvoidRedoKYC,
};
